import * as React from 'react';

import type { ApolloError } from 'apollo-client';

import type { ResponseWithId } from 'bundles/compound-assessments/components/local-state/changed-response/ResponseWithId';
import type { ChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import { typeNames } from 'bundles/compound-assessments/constants';
import type { ReviewResponseType } from 'bundles/compound-assessments/types/FormParts';
import { gradingCmlUtils } from 'bundles/grading-common';
import { GradingStepActionType, ReviewPartSchemaDetailsCourierTypeNameEnum } from 'bundles/grading/api/types';
import type {
  AssignmentRubric,
  BatchUpdateReviewPartResponsesInput,
  MultiLineInputReviewPartResponseInput,
  QuestionRubric,
  QuestionRubricElement,
  QuestionRubricPrompt,
  QuestionRubricPromptSchemaDetailsMultiLineInput,
  QuestionRubricResponseContainer,
} from 'bundles/grading/api/types';
import { getRubricId, getRubricPrompt, processNewResponseReviewPart } from 'bundles/grading/utils/formPartUtils';

export type ReviewPartInfo = {
  isRequired: boolean;
  isComplete: boolean;
};

export type ReviewPartProgress = {
  /**
   * Count of total review parts
   */
  totalParts: number;
  /**
   * Count of total required review parts
   */
  totalRequiredParts: number;
  /**
   * Count of completed required review parts
   */
  completedRequiredParts: number;
  /**
   * Count of completed review parts
   */
  completedParts: number;
  /**
   * If all review parts are completed.
   */
  areRubricsComplete: boolean;
  /**
   * Map to isRequired/isComplete info by rubricId
   */
  partDetails: { [key: string]: ReviewPartInfo };
};

export const defaultReviewPartProgress = (): ReviewPartProgress => ({
  totalParts: 0,
  totalRequiredParts: 0,
  completedParts: 0,
  completedRequiredParts: 0,
  areRubricsComplete: false,
  partDetails: {},
});

/**
 * Whether rubric is required or not
 * @param rubric  NewAssignmentRubric | NewQuestionRubric
 * @returns boolean
 */
export const isRubricRequired = (rubric: AssignmentRubric | QuestionRubric): boolean => {
  const { reviewPartSchemaDetails } = getRubricPrompt(rubric);

  switch (reviewPartSchemaDetails.courierTypeName) {
    case ReviewPartSchemaDetailsCourierTypeNameEnum.MultiLineInputReviewSchema:
      // rubric completion is required for both "graded textbox" and "non-graded textbox"
      return true;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.OptionsReviewSchema:
    case ReviewPartSchemaDetailsCourierTypeNameEnum.YesNoReviewSchema:
    case ReviewPartSchemaDetailsCourierTypeNameEnum.SingleLineInputReviewSchema:
      return true;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.BoxViewDocumentAnnotationReviewSchema:
      return false;
    default:
      break;
  }

  return false;
};

/**
 * Whether rubric feedback is complete considering some rubrics are optional
 * @param prompt QuestionRubricPrompt
 * @param reviewResponse ChangedResponse
 * @returns boolean
 */
export const isFeedbackComplete = (prompt: QuestionRubricPrompt, reviewResponse: ChangedResponse | ResponseWithId) => {
  if (reviewResponse && reviewResponse.response?.typeName === typeNames.REVIEW_RESPONSE) {
    const { reviewPartSchemaDetails } = prompt;
    const { reviewPart } = reviewResponse?.response?.definition;
    switch (reviewPart.typeName) {
      case typeNames.MULTILINEINPUT:
        if (
          reviewPartSchemaDetails.courierTypeName === typeNames.MULTILINEINPUTREVIEWSCHEMA &&
          (reviewPartSchemaDetails as QuestionRubricPromptSchemaDetailsMultiLineInput).points
        ) {
          // graded text box - only score is required to be completed
          if (reviewPart.definition.score !== null && reviewPart.definition.score !== undefined) {
            return true;
          }
        } else {
          // non-graded text box is required to be completed
          const richTextValue = reviewPart.definition.richTextInput?.definition.value;
          return Boolean(
            reviewPart.definition.input || (richTextValue && richTextValue !== gradingCmlUtils.EMPTY_CML_VALUE)
          );
        }
        break;
      case typeNames.OPTIONS:
      case typeNames.YESNO:
        if (reviewPart.definition.choice) {
          return true;
        }
        break;
      case typeNames.BOXVIEWDOCUMENTANNOTATION:
      case typeNames.SINGLELINEINPUT:
        // inline feedback is optional
        return true;
      default:
        break;
    }
  }

  return false;
};

/**
 * Calculates rubrics completion progress. The logic repeats `getCompletedFormCount` on the old SGA Grading page.
 * @param rubrics Question and Assignment level rubrics
 * @param responses Rubric changesets stored and updated in apollo cache
 * @returns ReviewPartProgress
 */
export const getReviewPartProgress = (
  rubrics: (AssignmentRubric | QuestionRubric)[],
  responses: ChangedResponse[] | ResponseWithId[]
): ReviewPartProgress => {
  const stats: ReviewPartProgress = { ...defaultReviewPartProgress(), totalParts: rubrics.length };

  // map from id to response for faster lookup later
  const responsesMap = new Map(responses.map((response) => [response.id, response]));

  rubrics.forEach((rubric) => {
    const rubricElement = rubric.formElementNode?.element as QuestionRubricElement;
    const reviewResponse = responsesMap.get(rubricElement.responseId);

    if (reviewResponse && reviewResponse.response?.typeName === typeNames.REVIEW_RESPONSE) {
      const rId = getRubricId(rubric);
      const prompt = getRubricPrompt(rubric);

      const isRequired = isRubricRequired(rubric);
      const isComplete = isFeedbackComplete(prompt, reviewResponse);
      stats.partDetails[rId] = { isRequired, isComplete };

      if (isRequired) {
        stats.totalRequiredParts += 1;
      }
      if (isComplete) {
        stats.completedParts += 1;
      }
      if (isRequired && isComplete) {
        stats.completedRequiredParts += 1;
      }
    }
  });

  stats.areRubricsComplete = stats.totalRequiredParts > 0 && stats.totalRequiredParts === stats.completedRequiredParts;
  return stats;
};

export type AutoSaveTaskState = {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: ApolloError | null;
  submitError: ApolloError | null;
};
export const defaultAutoSaveTaskState: AutoSaveTaskState = {
  isSaving: false,
  hasUnsavedChanges: false,
  saveError: null,
  isSubmitting: false,
  submitError: null,
};

export type AutoSaveContextType = {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  localScopeId: string;
  reviewPartProgress: ReviewPartProgress;
  autoSaveTaskStates: Record<string, AutoSaveTaskState>;
  forceSave: () => void;
};

export const AutoSaveContext = React.createContext({} as AutoSaveContextType);

type MutationPartialResponses = Pick<
  BatchUpdateReviewPartResponsesInput,
  'multiLineResponses' | 'optionsResponses' | 'boxResponses'
>;
/**
 * Converts array of `ChangedResponse` received from Apollo cache into collections of responses in format expected by Mutation
 * @param data ChangedResponse[]
 * @returns MutationPartialResponses
 */
export const constructResponseCollections = (data: ChangedResponse[]): MutationPartialResponses => {
  const res: MutationPartialResponses = {
    multiLineResponses: [],
    optionsResponses: [],
    boxResponses: [],
  };

  data.forEach((item) => {
    const reviewPart = (item.response as ReviewResponseType).definition.reviewPart;
    if (reviewPart.typeName === typeNames.OPTIONS) {
      res.optionsResponses.push({
        responseId: item.id,
        choices: reviewPart.definition.choice ? [reviewPart.definition.choice] : [],
        richTextInput: reviewPart.definition.richTextInput
          ? {
              dtdId: reviewPart.definition.richTextInput.definition.dtdId,
              cmlValue: reviewPart.definition.richTextInput.definition.value,
            }
          : null,
        graderType: (item.response as ReviewResponseType).definition.graderType,
        aiSuggestionId: (item.response as ReviewResponseType).definition.aiSuggestionId || null,
      });
    }
    if (reviewPart.typeName === typeNames.MULTILINEINPUT) {
      const value: MultiLineInputReviewPartResponseInput = {
        responseId: item.id,
        score: reviewPart.definition.score,
        graderType: (item.response as ReviewResponseType).definition.graderType,
        aiSuggestionId: (item.response as ReviewResponseType).definition.aiSuggestionId || null,
      };
      if (reviewPart.definition.input) {
        value.comment = reviewPart.definition.input;
      } else if (reviewPart.definition.richTextInput) {
        value.richTextComment = {
          dtdId: reviewPart.definition.richTextInput?.definition.dtdId,
          cmlValue: reviewPart.definition.richTextInput?.definition.value,
        };
      }
      res.multiLineResponses.push(value);
    }
    if (reviewPart.typeName === typeNames.BOXVIEWDOCUMENTANNOTATION) {
      res.boxResponses.push({
        responseId: item.id,
        fileId: reviewPart.definition.fileId,
        annotationComplete: reviewPart.definition.annotationComplete,
      });
    }
  });

  return res;
};

/**
 * Converts rubrics queried from the latest submission into format expected by `ChangedResponses` component
 * @param rubric
 * @returns ResponseWithId
 */
export const constructDefaultChangedResponse = (rubric: AssignmentRubric | QuestionRubric): ResponseWithId => {
  const element = rubric?.formElementNode?.element as QuestionRubricElement;
  const responseReviewPart = processNewResponseReviewPart(element.response as QuestionRubricResponseContainer);
  return {
    id: element.responseId,
    response: {
      typeName: typeNames.REVIEW_RESPONSE,
      definition: responseReviewPart,
    },
  };
};

const gradeBookActions: GradingStepActionType[] = [
  GradingStepActionType.StartUpdateAndSubmitDraft,
  GradingStepActionType.UpdateAndSubmitDraft,
  GradingStepActionType.SubmitLatestDraft,
];

/**
 * Can submit grades to grade book?
 * @param areRubricsComplete boolean are rubrics complete
 * @param allowedGradingActions from the sgaSubmission
 * @returns boolean
 */
export const canSubmitGrades = (
  areRubricsComplete: boolean,
  allowedGradingActions: GradingStepActionType[]
): boolean => {
  if (!areRubricsComplete) {
    return false;
  }
  const intersection = allowedGradingActions?.filter((item) => gradeBookActions.includes(item));
  return intersection ? intersection.length > 0 : false;
};
